import React, { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import HouseInfoWindow from "./HouseInfoWindow";
import HouseMarker from "./HouseMarker";
import { Skeleton } from "antd";

const libraries = ["places", "visualization"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const options = {
  zoomControl: true,
  fullscreenControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  scaleControl: true,
  rotateControl: true,
  gestureHandling: "greedy",
};

function calculateCentroid(houses) {
  if (!houses?.length) {
    return { lat: null, lng: null };
  }

  // Filter out invalid coordinates
  const validHouses = houses.filter(
    (house) =>
      typeof house.lat === "number" &&
      typeof house.lon === "number" &&
      !isNaN(house.lat) &&
      !isNaN(house.lon)
  );

  if (!validHouses.length) {
    return { lat: null, lng: null };
  }

  const total = validHouses.reduce(
    (acc, house) => ({
      lat: acc.lat + house.lat,
      lng: acc.lng + house.lon,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: total.lat / validHouses.length,
    lng: total.lng / validHouses.length,
  };
}

const HousesMap = ({
  houses,
  defaultCenter,
  address,
  isForecast = false,
  isDynamicZipCode = false,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAP_KEY,
    libraries,
  });
  const [centeri, setCenteri] = useState(null);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [geocodeError, setGeocodeError] = useState(null);

  const mapRef = useRef(null);
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMarkerClick = useCallback((house) => {
    setSelectedHouse(house);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedHouse(null);
  }, []);

  useEffect(() => {
    if (isLoaded && address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK") {
          setCenteri({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          });
          setGeocodeError(null);
        } else {
          setGeocodeError(`Geocoding failed: ${status}`);
          console.error("Geocode was not successful:", status);
        }
      });
    }
  }, [isLoaded, address]);

  const center = isDynamicZipCode
    ? calculateCentroid(houses)
    : defaultCenter ||
      centeri ||
      (houses?.[0]?.lat && houses?.[0]?.lon
        ? { lat: houses[0].lat, lng: houses[0].lon }
        : { lat: 0, lng: 0 });

  if (loadError || geocodeError) {
    return (
      <div className="map-error-container">
        <h3>Error loading Google Maps</h3>
        <p>Please check your internet connection and try again.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="map-loading-container">
        <Skeleton active />
        <p>Loading map...</p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ ...mapContainerStyle, cursor: "grab" }}
      zoom={16}
      center={center}
      options={options}
      onLoad={onMapLoad}
    >
      {(houses || []).map((house) => (
        <HouseMarker
          key={house.property_id}
          house={house}
          onClick={handleMarkerClick}
          isForecast={isForecast}
        />
      ))}
      {centeri && !isDynamicZipCode && <Marker position={centeri} />}
      {selectedHouse && (
        <HouseInfoWindow
          house={selectedHouse}
          isForecast={isForecast}
          onClose={handleInfoWindowClose}
        />
      )}
    </GoogleMap>
  );
};

export default HousesMap;
